// [ai] The navigation tree: /api/tree through the query catalog, sections in the user's order (Today · Read · Listen · Answer ·
// Chat · Vault), Read items under their group label, Listen folders nested by depth, the entry for the location marked. Folds
// live in localStorage under the old key (`atlas-tree-closed`) so they survive the page change a click causes — which, in the
// shell, is a client navigation: the tree never unmounts.
import { useCallback, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { useAstra } from "../chat/ChatUI";
import { useChat } from "../stores/chat";
import { useAtlasQuery } from "../api/query";
import { KEYS } from "../stores/shell";

export type TreeItem = { href: string; title: string; emoji?: string; group?: string; done?: boolean; count?: number | null; path?: string; depth?: number; sub?: string };
export type TreeSection = { id: string; name: string; href: string | null; items: TreeItem[]; busy?: boolean };
export type TreeData = { sections: TreeSection[] };

const readFolds = (): string[] => { try { const v = JSON.parse(localStorage.getItem(KEYS.folds) || "[]"); return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : []; } catch { return []; } };
const writeFolds = (f: string[]) => { try { localStorage.setItem(KEYS.folds, JSON.stringify(f)); } catch { /* private mode */ } };

/** A query-string href is on for its location plus more parameters (/chat?persona=coach is on at /chat?persona=coach&session=x). */
export const hrefOn = (href: string, loc: string) => loc === href || loc.startsWith(href + "&");
export function activeHref(items: TreeItem[], pathname: string, search: string): string | null {
  return items.find(it => it.href === pathname || (it.href.includes("?") && hrefOn(it.href, pathname + search)))?.href ?? null;
}

export function Tree() {
  const { pathname, search } = useLocation(); const navigate = useNavigate();
  const tree = useAtlasQuery<TreeData>("tree"); const astra = useAstra(); useChat(s => s.live);   // the chat links carry the persona's live session
  const [folds, setFolds] = useState(readFolds);
  const onToggle = useCallback((id: string, open: boolean) => { setFolds(f => { const next = open ? f.filter(x => x !== id) : f.includes(id) ? f : [...f, id]; writeFolds(next); return next; }); }, []);
  if (tree.isError) return <p className="at-embed-err">Navigation unavailable: {tree.error.message}</p>;
  if (!tree.data) return null;
  if (!tree.data.sections.length) return <p className="at-embed-err">Empty vault</p>;
  const link = (it: TreeItem) => {
    const on = it.href === pathname || (it.href.includes("?") && hrefOn(it.href, pathname + search));
    return (
      <Link key={it.href} className={"at-tree-a" + (on ? " on" : "")} to={astra.linkFor(it.href)} aria-current={on ? "page" : undefined}>
        {it.emoji ? <span className="at-tree-e">{it.emoji}</span> : null}<span className="at-tree-t">{it.title}</span>
        {it.done ? <span className="at-tree-done">✓</span> : null}{it.count != null ? <span className="at-tree-n">{it.count}</span> : null}
      </Link>
    );
  };
  const grouped = (items: TreeItem[]) => { let g: string | undefined; const out: React.ReactNode[] = []; for (const it of items) { if (it.group && it.group !== g) { g = it.group; out.push(<div key={"g:" + g} className="at-tree-g">{g}</div>); } out.push(link(it)); } return out; };
  const folders = (items: TreeItem[]) => {   // depth-nested <details>: a folder holds the folders whose path starts with its own
    let i = 0;
    const walk = (depth: number): React.ReactNode[] => { const out: React.ReactNode[] = []; while (i < items.length && (items[i]!.depth ?? 0) === depth) { const it = items[i++]!; const kids = i < items.length && (items[i]!.depth ?? 0) > depth ? walk(depth + 1) : []; out.push(kids.length ? <details key={it.href}><summary>{link(it)}</summary>{kids}</details> : link(it)); } return out; };
    return walk(0);
  };
  return (
    <nav className="at-tree" aria-label="Sections">
      {tree.data.sections.map(s => (
        <details key={s.id} data-section={s.id} open={!folds.includes(s.id)} onToggle={e => { if (e.target === e.currentTarget) onToggle(s.id, e.currentTarget.open); }}>
          <summary className="at-tree-h">{s.href ? <Link to={s.href}>{s.name}</Link> : s.name}{s.busy ? <span className="at-tree-busy" role="img" aria-label="working" /> : null}</summary>
          <div className="at-tree-body">
            {s.id === "listen" ? folders(s.items) : grouped(s.items)}
            {s.id === "today" ? <><div className="at-tree-sep" role="separator" /><button type="button" className="at-tree-a at-tree-vault" onClick={() => navigate("/browse")}><span className="at-tree-e">🗂️</span><span className="at-tree-t">Vault</span></button></> : null}
          </div>
        </details>
      ))}
    </nav>
  );
}
